import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bell,
  ChevronDown,
  Clock,
  Globe,
  Languages,
  Shield,
  Sparkles,
  TrendingUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import LanguageSelector from "@/components/LanguageSelector";
import SEOHead from "@/components/SEOHead";
import CountryStats from "@/components/CountryStats";
import ContactSection from "@/components/ContactSection";
import { WhyTedMonitorSection } from "@/components/WhyTedMonitorSection";

import { useAuth } from "@/hooks/useAuth";
import { useLocationDetection } from "@/hooks/useLocationDetection";
import { cn } from "@/lib/utils";
import euSearchIcon from "@/assets/eu-search-icon.png";

const NATIONAL_ADJECTIVE: Record<string, string> = {
  it: "Nazionali",
  en: "National",
  de: "Nationale",
  fr: "Nationales",
  es: "Nacionales",
  pt: "Nacionais",
  nl: "Nationale",
  pl: "Krajowe",
  ro: "Naționale",
  bg: "Национални",
  cs: "Národní",
  da: "Nationale",
  el: "Εθνικές",
  et: "Riiklikud",
  fi: "Kansalliset",
  ga: "Náisiúnta",
  hr: "Nacionalni",
  hu: "Nemzeti",
  lt: "Nacionaliniai",
  lv: "Nacionālie",
  mt: "Nazzjonali",
  sk: "Národné",
  sl: "Nacionalne",
  sv: "Nationella",
};

const BRAND = {
  logoMark: "/logo-mark.png",
  logoWord: "/logo.png",
};

export default function Index() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const locationData = useLocationDetection();

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const currentLang = i18n.language?.split("-")[0] || "it";
  const countryAdjective = NATIONAL_ADJECTIVE[currentLang] || "Nazionali";

  const heroHighlight = t("landing.heroHighlight", { country: countryAdjective });
  const subtitle = t("landing.subtitle");

  // ✅ SCHEMA MASTER UNIFICATO (@graph): Risolve definitivamente i conflitti SEO e i campi mancanti
  const structuredData = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://tedmonitor.eu/#organization",
          name: "TED Monitor",
          url: "https://tedmonitor.eu/",
          logo: "https://tedmonitor.eu/logo-mark.png",
          sameAs: ["https://linkedin.com/in/ted-monitor-6917b73a6"],
        },
        {
          "@type": "SoftwareApplication",
          "@id": "https://tedmonitor.eu/#software",
          name: "TED Monitor",
          operatingSystem: "Web",
          applicationCategory: "BusinessApplication",
          description: subtitle,
          image: "https://tedmonitor.eu/logo-mark.png",
          url: "https://tedmonitor.eu/",
          author: { "@id": "https://tedmonitor.eu/#organization" },
          offers: {
            "@type": "Offer",
            price: "0.00",
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            url: "https://tedmonitor.eu/auth",
            priceValidUntil: "2027-12-31",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            ratingCount: "12",
            reviewCount: "12",
            bestRating: "5",
            worstRating: "1",
          },
          review: {
            "@type": "Review",
            author: { "@type": "Person", name: "Luca B." },
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
            reviewBody:
              currentLang === "it"
                ? "Il miglior strumento per monitorare appalti europei. L'analisi IA risparmia ore di lavoro prezioso."
                : "The best tool for monitoring European procurement. AI analysis saves hours of valuable work.",
          },
        },
        {
          "@type": "FAQPage",
          "@id": "https://tedmonitor.eu/#faq",
          mainEntity: ["what", "how", "alerts", "languages", "trial", "security", "aiAnalysis", "dataExport"].map((key) => ({
            "@type": "Question",
            name: t(`landing.faq.items.${key}.question`),
            acceptedAnswer: {
              "@type": "Answer",
              text: t(`landing.faq.items.${key}.answer`),
            },
          })),
        },
      ],
    }),
    [subtitle, currentLang, t],
  );

  const features = [
    { icon: null, image: euSearchIcon, key: "search" },
    { icon: Bell, key: "alerts" },
    { icon: Sparkles, key: "ai" },
    { icon: Languages, key: "multiUser" },
    { icon: Shield, key: "secure" },
  ] as const;

  const stats = [
    { icon: Globe, value: "27", label: t("landing.stats.countries") },
    { icon: Languages, value: "24", label: t("landing.stats.languages") },
    { icon: TrendingUp, value: "500K+", label: t("landing.stats.tenders") },
    { icon: Clock, value: "24/7", label: t("landing.stats.monitoring") },
  ] as const;

  const faqItems = [
    { key: "what" },
    { key: "how" },
    { key: "alerts" },
    { key: "languages" },
    { key: "trial" },
    { key: "security" },
    { key: "aiAnalysis" },
    { key: "dataExport" },
  ] as const;

  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const toggleFaq = (key: string) => setOpenFaq(openFaq === key ? null : key);

  return (
    <>
      <SEOHead />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      <link rel="alternate" href="https://tedmonitor.eu/" hrefLang="x-default" />
      <link rel="alternate" href="https://tedmonitor.eu/" hrefLang="en" />
      <link rel="alternate" href="https://tedmonitor.eu/it" hrefLang="it" />

      <div className="min-h-screen bg-background overflow-hidden">
        {/* Sfondo Animato */}
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
          <div className="absolute top-0 left-1/4 w-48 h-48 bg-primary/10 rounded-full blur-3xl animate-pulse-soft" />
          <div
            className="absolute bottom-0 right-1/4 w-48 h-48 bg-accent/10 rounded-full blur-3xl animate-pulse-soft"
            style={{ animationDelay: "1s" }}
          />
        </div>

        {/* Header */}
        <header className="border-b bg-card/80 backdrop-blur-md sticky top-0 z-50">
          <nav className="container mx-auto px-4 py-2 flex items-center justify-between" aria-label="Main navigation">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md shadow-primary/25 group-hover:scale-110 transition-all overflow-hidden">
                <img src={BRAND.logoMark} alt="TED Monitor Logo" loading="eager" className="w-7 h-7 object-contain" />
              </div>
              <span className="text-lg font-bold text-foreground">{t("landing.title")}</span>
            </Link>

            <div className="flex items-center gap-2">
              <LanguageSelector />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(currentLang === "it" ? "/it/prezzi" : "/pricing")}
                className="hidden sm:inline-flex h-8 text-xs"
              >
                {t("nav.pricing")}
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/auth")}
                className="h-8 text-xs shadow-md shadow-primary/25 hover:shadow-primary/40 transition-all"
              >
                {t("auth.login")}
              </Button>
            </div>
          </nav>
        </header>

        <main>
          {/* Hero Section */}
          <section className="py-8 lg:py-12 relative" aria-labelledby="main-headline">
            <div className="container mx-auto px-4">
              <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-4">
                    <Globe className="w-3.5 h-3.5" />
                    {t("landing.badge")}
                  </div>

                  <h1
                    id="main-headline"
                    className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground mb-4 leading-tight"
                  >
                    {t("landing.heroTitle")}{" "}
                    <span className="block bg-gradient-to-r from-primary via-primary/80 to-accent bg-clip-text text-transparent">
                      {heroHighlight}
                    </span>
                  </h1>

                  <p className="text-base md:text-lg text-muted-foreground mb-6 max-w-xl mx-auto lg:mx-0">{subtitle}</p>

                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-center lg:justify-start">
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="h-12 px-8 text-base shadow-lg shadow-primary/30 group"
                    >
                      {t("landing.getStarted")}
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>

                    <div className="relative">
                      <Link
                        to={currentLang === "it" ? "/it/analisi-risultati-ai" : "/ai-results-analysis"}
                        className="absolute -top-4 -right-4 z-10"
                      >
                        <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-accent text-primary-foreground text-[10px] font-bold shadow-lg animate-bounce-soft">
                          <Sparkles className="w-3 h-3" /> AI
                        </div>
                      </Link>
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() =>
                          navigate(
                            currentLang === "it"
                              ? "/it/software-appalti-pubblici-europei"
                              : "/european-public-procurement-software",
                          )
                        }
                        className="h-12 px-8 text-base"
                      >
                        {t("landing.cta.software")}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Product Mockup – realistic tender results */}
                <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
                  <Card className="border-0 shadow-2xl bg-card/80 backdrop-blur-sm overflow-hidden transform hover:scale-[1.02] transition-transform duration-500">
                    <CardContent className="p-0">
                      <div className="px-4 py-3 border-b bg-muted/20 flex items-center justify-between">
                        <div className="flex gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-red-400/50" />
                          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/50" />
                          <div className="w-2.5 h-2.5 rounded-full bg-green-400/50" />
                        </div>
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                          {t("landing.mockTitle")}
                        </div>
                      </div>
                      <div className="p-3 bg-background/40 space-y-2 max-h-[260px] overflow-hidden relative">
                        {[
                          { flag: "🇮🇹", title: "Fornitura dispositivi medici ospedalieri", buyer: "ASL Roma 1", value: "€ 2.400.000", cpv: "33100000" },
                          { flag: "🇩🇪", title: "Lieferung medizinischer Geräte", buyer: "Stadt München", value: "€ 1.850.000", cpv: "33190000" },
                          { flag: "🇫🇷", title: "Services de conseil en informatique", buyer: "Ministère de l'Intérieur", value: "€ 3.200.000", cpv: "72000000" },
                          { flag: "🇪🇸", title: "Obras de construcción de carreteras", buyer: "Ayuntamiento de Madrid", value: "€ 5.100.000", cpv: "45233120" },
                          { flag: "🇵🇱", title: "Dostawa sprzętu komputerowego", buyer: "Urząd Miasta Warszawa", value: "€ 890.000", cpv: "30200000" },
                        ].map((tender, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-3 p-2.5 rounded-lg border border-border/60 bg-card/60 hover:border-primary/30 transition-colors"
                          >
                            <span className="text-lg mt-0.5">{tender.flag}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-foreground truncate">{tender.title}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{tender.buyer}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs font-bold text-primary">{tender.value}</p>
                              <p className="text-[9px] text-muted-foreground">CPV {tender.cpv}</p>
                            </div>
                          </div>
                        ))}
                        {/* Gradient fade at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-card/90 to-transparent pointer-events-none" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="py-6 border-y bg-muted/30" aria-label="Quick statistics">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map(({ icon: Icon, value, label }, index) => (
                  <div key={index} className="text-center p-2">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary mb-2">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="text-xl md:text-2xl font-bold text-foreground">{value}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features-section" className="py-12 scroll-mt-20">
            <div className="container mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
                {t("landing.featuresTitle")}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {features.map((feature, index) => {
                  const { icon: Icon, key } = feature;
                  const image = 'image' in feature ? feature.image : null;
                  const isItalian = currentLang === "it";
                  const linkMap: Record<string, { en: string; it: string }> = {
                    search: { en: "/eu-tender-monitoring", it: "/it/monitoraggio-bandi-ue" },
                    alerts: { en: "/ted-procurement-alerts", it: "/it/alert-bandi-ted" },
                    multiUser: { en: "/multilingual-access", it: "/it/accesso-multilingue" },
                    secure: { en: "/compliance-and-security", it: "/it/conformita-sicurezza" },
                    ai: { en: "/ai-results-analysis", it: "/it/analisi-risultati-ai" },
                  };
                  const featureLink = linkMap[key]?.[isItalian ? "it" : "en"];

                  return (
                    <Link to={featureLink} key={key} className="group">
                      <Card className="h-full border border-transparent hover:border-primary/20 hover:shadow-xl transition-all duration-300 bg-card/50 backdrop-blur-sm overflow-hidden">
                        <CardContent className="p-6">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform overflow-hidden">
                            {image ? (
                              <img src={image} alt="EU Search" className="w-8 h-8 object-contain" loading="lazy" />
                            ) : Icon ? (
                              <Icon className="w-5 h-5 text-primary" />
                            ) : null}
                          </div>
                          <h3 className="text-base font-bold text-foreground mb-2">
                            {t(`landing.features.${key}.title`)}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {t(`landing.features.${key}.description`)}
                          </p>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          <WhyTedMonitorSection
            links={
              currentLang === "it"
                ? {
                    monitoring: "/it/monitoraggio-bandi-ue",
                    alerts: "/it/alert-bandi-ted",
                    software: "/it/software-appalti-pubblici-europei",
                    multilingual: "/it/accesso-multilingue",
                    security: "/it/conformita-sicurezza",
                    ai: "/it/analisi-risultati-ai",
                  }
                : {
                    monitoring: "/eu-tender-monitoring",
                    alerts: "/ted-procurement-alerts",
                    software: "/european-public-procurement-software",
                    multilingual: "/multilingual-access",
                    security: "/compliance-and-security",
                    ai: "/ai-results-analysis",
                  }
            }
          />

          <CountryStats detectedCountry={locationData.country} />

          {/* FAQ Section */}
          <section className="py-12 bg-muted/20" aria-labelledby="faq-title">
            <div className="container mx-auto px-4">
              <h2 id="faq-title" className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
                {t("landing.faq.title")}
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqItems.map(({ key }, index) => (
                  <div key={key} className="border border-border bg-card rounded-xl overflow-hidden shadow-sm">
                    <button
                      onClick={() => toggleFaq(key)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/30 transition-colors"
                    >
                      <span className="text-base font-semibold text-foreground">
                        {t(`landing.faq.items.${key}.question`)}
                      </span>
                      <ChevronDown
                        className={cn(
                          "w-5 h-5 text-muted-foreground transition-transform duration-300",
                          openFaq === key && "rotate-180",
                        )}
                      />
                    </button>
                    <div
                      className={cn(
                        "overflow-hidden transition-all duration-300 ease-in-out",
                        openFaq === key ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0",
                      )}
                    >
                      <p className="px-5 pb-5 text-sm md:text-base text-muted-foreground leading-relaxed border-t border-border/50 pt-4">
                        {t(`landing.faq.items.${key}.answer`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <ContactSection />
        </main>

        {/* Footer */}
        <footer className="py-12 border-t bg-card/80">
          <div className="container mx-auto px-4 text-center md:text-left">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
              <div className="col-span-1 md:col-span-1">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                  <img src={BRAND.logoMark} alt="TED Monitor logo mark" className="w-6 h-6" />
                  <span className="font-bold text-lg">TED Monitor</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed mb-4">{t("landing.disclaimer")}</p>
              </div>

              <div className="text-sm">
                <h4 className="font-bold mb-4">{t("footer.product", { defaultValue: "Prodotto" })}</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>
                    <Link
                      to={
                        currentLang === "it"
                          ? "/it/software-appalti-pubblici-europei"
                          : "/european-public-procurement-software"
                      }
                      className="hover:text-primary transition-colors"
                    >
                      {currentLang === "it"
                        ? "Monitoraggio e ricerca appalti europei"
                        : "European Procurement Software"}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={currentLang === "it" ? "/it/prezzi" : "/pricing"}
                      className="hover:text-primary transition-colors"
                    >
                      {currentLang === "it" ? "Costi software monitoraggio appalti" : "Pricing"}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={currentLang === "it" ? "/it/analisi-risultati-ai" : "/ai-results-analysis"}
                      className="hover:text-primary transition-colors"
                    >
                      {currentLang === "it" ? "Analisi AI dei bandi" : "AI Analysis"}
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="text-sm">
                <h4 className="font-bold mb-4">{t("footer.legal", { defaultValue: "Legale" })}</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>
                    <Link to="/privacy-policy" className="hover:text-primary transition-colors">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/cookie-policy" className="hover:text-primary transition-colors">
                      Cookie Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms-conditions" className="hover:text-primary transition-colors">
                      Termini e Condizioni
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="text-sm">
                <h4 className="font-bold mb-4">Social</h4>
                <a
                  href="https://linkedin.com/in/ted-monitor-6917b73a6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center md:justify-start gap-2 text-muted-foreground hover:text-[#0A66C2] transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>
            <div className="pt-8 border-t border-border/50 text-center text-[10px] text-muted-foreground uppercase tracking-widest">
              © 2026 TED Monitor. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
